import { useDefaultValueLabel } from 'common/utils/defaultValueLabel'
import { fNumber } from 'common/utils/formatNumber'
import ActionPill from 'modules/inventory/components/ActionPill'
import { StockHistoryV3 } from 'modules/inventory/api/type'
import { formatThaiShortBEWithTime } from 'modules/inventory/helper/format_date'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'

import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import { PATH_DASHBOARD } from 'routes/paths'

interface StockHistoryDetailDialogProps {
  open: boolean
  onClose: () => void
  item: StockHistoryV3 | null
}

function StockHistoryDetailDialog({ open, onClose, item }: StockHistoryDetailDialogProps) {
  const { t } = useTranslation('stock')
  const label = useDefaultValueLabel()
  const navigate = useNavigate()
  if (!item) return null

  const qty = item.used_qty
  const isEditStock = item.transaction_type === 'edit_stock'
  const qtyColor = qty < 0 ? 'error.main' : qty > 0 ? 'success.main' : 'text.primary'

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography variant="subtitle1" fontWeight={700}>{t('stock.detail')}</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">{t('stock.history.create')}</Typography>
          <Typography variant="body2" fontWeight={600}>{formatThaiShortBEWithTime(item.created_at)}</Typography>
        </Stack>

        <Card
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: 1,
            mb: 2,
            bgcolor: '#F5F5F5',
            borderColor: '#EAEAEA',
            border: 'none',
            boxShadow: 'none',
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack spacing={1}>
              <Box sx={{ display: 'flex' }}>
                <ActionPill action={item.transaction_type} size="small" />
              </Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1C2434' }}>
                {item.product_name}
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="baseline" spacing={0.5}>
              {isEditStock ? (
                <Typography variant="h4" fontWeight={700} sx={{ color: 'text.secondary', lineHeight: 1 }}>-</Typography>
              ) : (
                <>
                  <Typography variant="h4" fontWeight={700} sx={{ color: qtyColor, lineHeight: 1 }}>
                    {qty > 0 ? `+${fNumber(qty)}` : fNumber(qty)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'gray', fontWeight: 500 }}>
                    {item.unit_abbreviation}
                  </Typography>
                </>
              )}
            </Stack>
          </Stack>
        </Card>

        {[
          { label: t('stock.history.transactionDate'), value: item.transaction_date ? formatThaiShortBEWithTime(item.transaction_date) : '-' },
          { label: t('stock.history.lot'), value: item.stock_lot_number || '-' },
          { label: t('stock.history.category'), value: label.inventoryCategory(item.category_name) || '-' },
          { label: t('stock.warehouse'), value: item.warehouse_name || '-' },
        ].map(({ label, value }) => (
          <Stack key={label} direction="row" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="body2" fontWeight={600}>{value}</Typography>
          </Stack>
        ))}

        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Typography variant="body2" color="text.secondary">{t('stock.remain')}</Typography>
          <Typography variant="body2" fontWeight={600} sx={{ color: item.remaining_qty < 0 ? 'error.main' : 'text.primary' }}>
            {fNumber(item.remaining_qty)} {item.unit_abbreviation}
          </Typography>
        </Stack>

        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">{t('stock.history.remark')}</Typography>
          <Typography variant="body2" fontWeight={600}>{item.remark || '-'}</Typography>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          variant="contained"
          onClick={() => { navigate(`${PATH_DASHBOARD.inventory.product}/${item.product_id}`); onClose() }}
        >
          {t('stock.history.view_product')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default StockHistoryDetailDialog
